import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import logger from "../../utils/logger";


interface RedirectParams {
  ref?: string;
  [key: string]: string | undefined;
}

const AppRedirectScreen = () => {
  const history = useHistory();
  const location = useLocation();
  const [isRedirecting, setIsRedirecting] = useState(true);
  const [deviceType, setDeviceType] = useState<string>("unknown");

  // App Store URLs (replace with your actual app URLs)
  const APP_STORE_URL = "https://apps.apple.com/app/your-app-id";
  const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=your.package.name";
  const DESKTOP_URL = "https://your-website.com/download";

  // Enhanced device detection
  const detectDevice = (): string => {
    const userAgent = navigator.userAgent.toLowerCase();

    // iOS detection
    if (/iphone|ipad|ipod/.test(userAgent)) {
      return "ios";
    }

    // Android detection
    if (/android/.test(userAgent)) {
      return "android";
    }

    // Windows Phone detection
    if (/windows phone/.test(userAgent)) {
      return "android"; // Redirect to Google Play
    }

    // Desktop detection
    if (!/mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent)) {
      return "desktop";
    }

    return "unknown";
  };

  // Get redirect URL based on device type
  const getRedirectURL = (device: string): string => {
    switch (device) {
      case "ios":
        return APP_STORE_URL;
      case "android":
        return GOOGLE_PLAY_URL;
      case "desktop":
        return DESKTOP_URL;
      default:
        return DESKTOP_URL; // Fallback
    }
  };

  // Get store name for display
  const getStoreName = (device: string): string => {
    switch (device) {
      case "ios":
        return "App Store";
      case "android":
        return "Google Play Store";
      case "desktop":
        return "Web Download";
      default:
        return "App Store";
    }
  };

  useEffect(() => {
    const device = detectDevice();
    setDeviceType(device);

    // Get URL parameters
    const searchParams = new URLSearchParams(location.search);
    const params: RedirectParams = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });

    // Log analytics if needed
    if (params.ref) {
      logger.log("QR Code redirect:", { device, ref: params.ref });
      // Here you could send analytics data to your tracking service
    }

    // Redirect after a short delay to show the loading state
    const redirectURL = getRedirectURL(device);

    setTimeout(() => {
      // Try to redirect in the same tab first
      window.location.href = redirectURL;

      // Fallback: open in new tab if redirect fails
      setTimeout(() => {
        window.open(redirectURL, "_blank");
      }, 1000);
    }, 1500);
  }, [location.search]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="text-center max-w-md mx-auto p-8">
        <div className="mb-8">
          <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Redirecting to {getStoreName(deviceType)}...</h1>
          <p className="text-gray-600 mb-4">
            We detected a {deviceType} device and are taking you to the appropriate store.
          </p>
        </div>

        <div className="space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>

          <div className="text-sm text-gray-500">
            <p>If you're not redirected automatically,</p>
            <button
              onClick={() => window.open(getRedirectURL(deviceType), "_blank")}
              className="text-blue-600 hover:text-blue-800 underline mt-1"
            >
              click here to continue
            </button>
          </div>
        </div>

        {/* Device Info for debugging */}
        <details className="mt-8 text-xs text-gray-400">
          <summary className="cursor-pointer">Debug Info</summary>
          <div className="mt-2 p-2 bg-gray-100 rounded text-left">
            <p>
              <strong>Device Type:</strong> {deviceType}
            </p>
            <p>
              <strong>User Agent:</strong> {navigator.userAgent}
            </p>
            <p>
              <strong>Redirect URL:</strong> {getRedirectURL(deviceType)}
            </p>
          </div>
        </details>
      </div>
    </div>
  );
};

export default AppRedirectScreen;
